import { useNutritionResource, notifyNutritionChanged } from "@/hooks/useNutritionResource";
import { supabase } from "@/integrations/supabase/client";

/**
 * W6 Nutrition Mini-Mirror snapshot — single RPC round-trip.
 *
 * Calls `public.get_nutrition_snapshot(p_user_id, p_tz)` which returns:
 *   {
 *     energyScore, todayMealsCount, weekMealsCount,
 *     todayHydrationCups, hydrationGoal,
 *     todayMacros: { kcal, protein, carbs, fat },
 *     energyComponents: { hydrationPct, proteinPct, cadencePct },
 *     lastMeal | null,
 *     chips: { today: NutritionTodaySummary[], week: NutritionWeekDaySummary[],
 *              hydration: NutritionHydrationDaySummary[] }
 *   }
 *
 * Account-bound reads refresh on mutations, local midnight and foreground
 * recovery. The RPC receives the current browser timezone.
 *
 * Rename note: "Charge" (estrangeirismo) retired by Rafa 12/05 in favour of
 * ENERGIA/ENERGY/ENERGIE. Composite formula preserved
 * (50%hyd + 30%prot + 20%cadence).
 */

export interface NutritionTodaySummary {
  id: string;
  description: string | null;
  meal_type: string | null;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  source: string | null;
  created_at: string;
}

export interface NutritionWeekDaySummary {
  day: string;
  meals_count: number;
  kcal_total: number;
  protein_total: number;
}

export interface NutritionHydrationDaySummary {
  day: string;
  cups: number;
  goal: number;
  pct: number;
}

export interface NutritionLastMeal {
  id: string;
  description: string | null;
  meal_type: string | null;
  kcal: number;
  source: string | null;
  eaten_at: string;
}

export interface NutritionSnapshotData {
  energyScore: number;
  todayMealsCount: number;
  weekMealsCount: number;
  todayHydrationCups: number;
  hydrationGoal: number;
  todayMacros: { kcal: number; protein: number; carbs: number; fat: number };
  energyComponents: { hydrationPct: number; proteinPct: number; cadencePct: number };
  lastMeal: NutritionLastMeal | null;
  chips: {
    today: NutritionTodaySummary[];
    week: NutritionWeekDaySummary[];
    hydration: NutritionHydrationDaySummary[];
  };
}

const EMPTY: NutritionSnapshotData = {
  energyScore: 0,
  todayMealsCount: 0,
  weekMealsCount: 0,
  todayHydrationCups: 0,
  hydrationGoal: 8,
  todayMacros: { kcal: 0, protein: 0, carbs: 0, fat: 0 },
  energyComponents: { hydrationPct: 0, proteinPct: 0, cadencePct: 0 },
  lastMeal: null,
  chips: { today: [], week: [], hydration: [] },
};

export interface NutritionSnapshot {
  data: NutritionSnapshotData;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}
async function loadSnapshot(userId: string, assertCurrent: () => void): Promise<NutritionSnapshotData> {
  const { data: rpcData, error } = await (supabase as any).rpc("get_nutrition_snapshot", {
    p_user_id: userId, p_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  assertCurrent(); if (error) throw error;
  if (!rpcData || typeof rpcData !== "object") throw new Error("Nutrition snapshot missing");
      const value: NutritionSnapshotData = {
        energyScore: Number(rpcData?.energyScore ?? 0),
        todayMealsCount: Number(rpcData?.todayMealsCount ?? 0),
        weekMealsCount: Number(rpcData?.weekMealsCount ?? 0),
        todayHydrationCups: Number(rpcData?.todayHydrationCups ?? 0),
        hydrationGoal: Number(rpcData?.hydrationGoal ?? 8),
        todayMacros: {
          kcal: Number(rpcData?.todayMacros?.kcal ?? 0),
          protein: Number(rpcData?.todayMacros?.protein ?? 0),
          carbs: Number(rpcData?.todayMacros?.carbs ?? 0),
          fat: Number(rpcData?.todayMacros?.fat ?? 0),
        },
        energyComponents: {
          hydrationPct: Number(rpcData?.energyComponents?.hydrationPct ?? 0),
          proteinPct: Number(rpcData?.energyComponents?.proteinPct ?? 0),
          cadencePct: Number(rpcData?.energyComponents?.cadencePct ?? 0),
        },
        lastMeal: (rpcData?.lastMeal ?? null) as NutritionLastMeal | null,
        chips: {
          today: Array.isArray(rpcData?.chips?.today) ? rpcData.chips.today : [],
          week: Array.isArray(rpcData?.chips?.week) ? rpcData.chips.week : [],
          hydration: Array.isArray(rpcData?.chips?.hydration) ? rpcData.chips.hydration : [],
        },
      };
  return value;
}
export function useNutritionSnapshot(): NutritionSnapshot {
  const resource = useNutritionResource(loadSnapshot);
  return { data: resource.data ?? EMPTY, isLoading: resource.status === "loading",
    error: resource.status === "error" || resource.status === "signed-out" ? "nutrition.error" : null,
    refresh: resource.refresh };
}
export function invalidateNutritionSnapshotCache(): void { notifyNutritionChanged(); }
